import type {
  PolicyScenario,
  PolicyLimits,
} from '@/data/subsidy/policies/calculate-course-refundable-amount/interfaces';
import { formatMoney } from '@/utils/format-money';
import { parsePolicyLimits } from '@/utils/parse-policy-limits';

type RefundPolicyTextsParams = {
  policyLimits: PolicyLimits;
  refundableAmount: number;
};

type RefundPolicyTexts = {
  title: string;
  subtitle: string;
  linkLabel: string;
};

export const refundPolicyTexts = ({
  policyLimits,
  refundableAmount,
}: RefundPolicyTextsParams): RefundPolicyTexts => {
  const { percentageLimit, amountLimitPerPurchase } =
    parsePolicyLimits(policyLimits);

  const formattedValue = formatMoney(refundableAmount);
  const scenario: PolicyScenario | null = policyLimits.policy_scenario;

  const linkLabel = {
    seeRules: 'Ver regras',
    seeRulesAndBalance: 'Ver regras e saldo disponível',
  };

  const scenarios: Record<PolicyScenario, RefundPolicyTexts> = {
    cycle_balance_only: {
      title: `Receba até ${formattedValue} de reembolso`,
      subtitle: 'De acordo com as regras de reembolso de sua empresa.',
      linkLabel: linkLabel.seeRulesAndBalance,
    },
    purchase_cap_only: {
      title: `Receba ${formattedValue} de reembolso por mês`,
      subtitle: `Sua empresa reembolsa até ${formatMoney(amountLimitPerPurchase)} do valor da parcela.`,
      linkLabel: linkLabel.seeRules,
    },
    percentage_only: {
      title: `Receba ${formattedValue} de reembolso por mês`,
      subtitle: `Sua empresa reembolsa até ${percentageLimit}% do valor da parcela.`,
      linkLabel: linkLabel.seeRules,
    },
    percentage_and_purchase_cap: {
      title: `Receba ${formattedValue} de reembolso por mês`,
      subtitle: `Sua empresa reembolsa até ${percentageLimit}% do valor, limitado a ${formatMoney(amountLimitPerPurchase)} por mês.`,
      linkLabel: linkLabel.seeRules,
    },
    percentage_and_cycle_balance: {
      title: `Receba ${formattedValue} de reembolso por mês`,
      subtitle: `Sua empresa reembolsa até ${percentageLimit}% do valor de acordo com as regras de sua empresa.`,
      linkLabel: linkLabel.seeRulesAndBalance,
    },
    purchase_cap_and_cycle_balance: {
      title: `Receba ${formattedValue} de reembolso por mês`,
      subtitle: `Sua empresa reembolsa até ${formatMoney(amountLimitPerPurchase)} do valor da parcela, de acordo com as regras de sua empresa.`,
      linkLabel: linkLabel.seeRulesAndBalance,
    },
    all_limits: {
      title: `Receba ${formattedValue} de reembolso por mês`,
      subtitle: `Sua empresa reembolsa até ${percentageLimit}% do valor, limitado a ${formatMoney(amountLimitPerPurchase)} por mês. De acordo com as regras de sua empresa.`,
      linkLabel: linkLabel.seeRulesAndBalance,
    },
  };

  return scenario
    ? scenarios[scenario]
    : {
        title: `Receba ${formattedValue} de reembolso`,
        subtitle: 'De acordo com as regras de reembolso de sua empresa.',
        linkLabel: 'Ver regras',
      };
};
